import type { MisconceptionDefinition } from '../types';

/**
 * TIJD — Snelheid / afstand / gemiddelde / tijdzones sub-registry.
 *
 * Misconcepties rond rekenen MET tijd in toegepaste context: de
 * Afstand-Snelheid-Tijd-driehoek, gemiddelde snelheid bij meerdere
 * trajecten (inclusief de klassieke "rekenkundig gemiddelde"-val),
 * en tijdzone-conversies inclusief de 24-uurs grens.
 *
 * Families: TIJD-SNELHEID, TIJD-AFSTAND, TIJD-GEMIDDELDE, TIJD-TIJDZONES.
 *
 * Onderdeel van de TIJD-barrel-splitsing (juni 2026).
 */
export const TIJD_SNELHEID_MISCONCEPTIONS: MisconceptionDefinition[] = [
// ─── Formule / driehoek-fouten ──────────────────────────────────────────
{
  id: 'tijd.speed-formula-confused',
  label: 'Snelheidsformule verward',
  module: 'Tijd',
  description: 'Leerling gebruikt de verkeerde bewerking (bijv. vermenigvuldigen in plaats van delen) bij de AST-driehoek.',
  typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE']
}, {
  id: 'tijd.minutes-to-hours-error',
  label: 'Minuten naar uren fout',
  module: 'Tijd',
  description: 'Leerling rekent minuten verkeerd om naar decimale uren (bijv. 15 min = 0,15 uur in plaats van 0,25 uur).',
  typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE']
},
// ─── Snelheid berekenen — operatie- & eenheidsfouten ────────────────────
{
  id: 'tijd.snelheid-als-afstand',
  label: 'Snelheid verward met afstand',
  module: 'Tijd',
  description: 'Leerling geeft de afgelegde afstand (of het afstandsgetal uit de vraag) als snelheid, zonder te delen door de tijd.',
  typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID']
}, {
  id: 'tijd.snelheid-vermenigvuldigen',
  label: 'Vermenigvuldigt i.p.v. delen bij snelheid',
  module: 'Tijd',
  description: 'Leerling berekent afstand × tijd in plaats van afstand ÷ tijd bij gemiddelde snelheid.',
  typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID']
}, {
  id: 'tijd.eenheden-optellen',
  label: 'Km en uren bij elkaar opgeteld',
  module: 'Tijd',
  description: 'Leerling telt afstand en tijd direct bij elkaar op (bijv. 30 km + 2 uur = 32) en negeert de verschillende eenheden.',
  typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID', 'TIJD-AFSTAND']
}, {
  id: 'tijd.komma-tijd-negeren',
  label: 'Kommadeel van tijd genegeerd',
  module: 'Tijd',
  description: 'Leerling negeert het kommadeel van de tijd (bijv. 2,5 uur → 2 uur) bij snelheidsberekeningen.',
  typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID']
}, {
  id: 'tijd.minuten-niet-omrekenen',
  label: 'Minuten niet omgerekend naar uren',
  module: 'Tijd',
  description: 'Leerling rekent met minuten alsof het uren zijn bij snelheid in km/u (bijv. 50 min → deelt door 50 i.p.v. 5/6).',
  typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID']
}, {
  id: 'tijd.snelheid-als-tijd',
  label: 'Snelheid verward met tijd in minuten',
  module: 'Tijd',
  description: 'Leerling neemt het minuten-getal uit de vraag direct als snelheid (bijv. 50 min → 50 km/u).',
  typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID']
},
// ─── Gemiddelde snelheid — meerdere trajecten ──────────────────────────
{
  id: 'tijd.snelheid-gemiddeld-naief',
  label: 'Rekenkundig gemiddelde van snelheden',
  module: 'Tijd',
  description: 'Leerling neemt (v1 + v2) ÷ 2 als gemiddelde snelheid, ook als de twee stukken niet even lang duren. Werkt alleen bij gelijke tijden, niet bij gelijke afstanden.',
  typicalFamilies: ['TIJD-GEMIDDELDE']
}, {
  id: 'tijd.snelheid-optellen',
  label: 'Snelheden bij elkaar opgeteld',
  module: 'Tijd',
  description: 'Leerling telt twee snelheden direct bij elkaar op (bijv. 40 + 60 = 100 km/u) als "gemiddelde".',
  typicalFamilies: ['TIJD-GEMIDDELDE']
}, {
  id: 'tijd.snelheid-gemiddeld-rekenfout',
  label: 'Goede aanpak, rekenfout in tussenstap',
  module: 'Tijd',
  description: 'Leerling gebruikt de juiste formule (totale afstand ÷ totale tijd) maar maakt een rekenfout in een tussenstap.',
  typicalFamilies: ['TIJD-GEMIDDELDE']
}, {
  id: 'tijd.snelheid-stilstand-aftrekken',
  label: 'Stilstand afgetrokken van reistijd',
  module: 'Tijd',
  description: 'Leerling trekt de stilstandtijd af van de totale reistijd bij gemiddelde reissnelheid, terwijl stilstand juist meetelt.',
  typicalFamilies: ['TIJD-GEMIDDELDE']
},
// ─── Tijdzones ──────────────────────────────────────────────────────────
{
  id: 'tijd.timezone-direction-confused',
  label: 'Tijdzone richting verward',
  module: 'Tijd',
  description: 'Leerling telt uren op in plaats van af te trekken bij tijdzones, of andersom.',
  typicalFamilies: ['TIJD-TIJDZONES']
}, {
  id: 'tijd.timezone-midnight-error',
  label: 'Tijdzone over middernacht',
  module: 'Tijd',
  description: 'Leerling houdt geen rekening met de 24-uurs grens bij tijdzones.',
  typicalFamilies: ['TIJD-TIJDZONES']
}, {
  id: 'tijd.timezone-flight-error',
  label: 'Tijdverschil vergeten bij vlucht',
  module: 'Tijd',
  description: 'Leerling vergeet het tijdverschil op te tellen of af te trekken bij het berekenen van de aankomsttijd na een vlucht.',
  typicalFamilies: ['TIJD-TIJDZONES']
}];